import type { Chef } from '../types';
import { getChefWebsites, normalizeInstagramUsername, extractInstagramUsername } from './chefDisplay';
import { parseInstagramFullName, chefNameLooksLikeRawInstagramFullName } from './instagramChefProfile';

function pickFirstNonEmpty(...values: Array<string | undefined>): string | undefined {
    for (const value of values) {
        const trimmed = value?.trim();
        if (trimmed) return trimmed;
    }
    return undefined;
}

export function resolveChefInstagramUsername(
    chef: Pick<Chef, 'instagramUsername' | 'websites' | 'website' | 'englishName'>,
): string | null {
    const fromField = normalizeInstagramUsername(chef.instagramUsername);
    if (fromField) return fromField;

    for (const link of getChefWebsites(chef)) {
        const fromLink = extractInstagramUsername(link);
        if (fromLink) return fromLink;
    }

    return normalizeInstagramUsername(chef.englishName);
}

export function combineChefWebsites(...chefs: Array<Pick<Chef, 'websites' | 'website'>>): string[] {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const chef of chefs) {
        for (const link of getChefWebsites(chef)) {
            const key = link.toLowerCase();
            if (seen.has(key)) continue;
            seen.add(key);
            result.push(link);
        }
    }
    return result;
}

export interface ChefMergePreview {
    description?: string;
    imageUrl?: string;
    englishName: string;
    name: string;
    websites: string[];
    instagramUsername?: string;
    instagramUserId?: string;
    linkedUserId?: string;
    originCountry?: string;
}

/** Computes merged field values for the chef being kept. */
export function previewChefMerge(keep: Chef, merge: Chef): ChefMergePreview {
    let name = keep.name?.trim() || merge.name?.trim() || keep.englishName;
    let description = pickFirstNonEmpty(keep.description, merge.description);

    if (chefNameLooksLikeRawInstagramFullName(name, name)) {
        const parsed = parseInstagramFullName(name, keep.englishName);
        name = parsed.name || name;
        description = pickFirstNonEmpty(keep.description, parsed.description, merge.description);
    } else if (!keep.description?.trim() && merge.name?.trim() && !chefNameLooksLikeRawInstagramFullName(merge.name, merge.name)) {
        name = pickFirstNonEmpty(keep.name, merge.name) || name;
    }

    const instagramUsername =
        resolveChefInstagramUsername(keep) ||
        resolveChefInstagramUsername(merge) ||
        undefined;

    const instagramUserId = pickFirstNonEmpty(keep.instagramUserId, merge.instagramUserId);

    return {
        name,
        englishName: pickFirstNonEmpty(keep.englishName, merge.englishName) || keep.id,
        description,
        imageUrl: pickFirstNonEmpty(keep.imageUrl, merge.imageUrl),
        websites: combineChefWebsites(keep, merge),
        instagramUsername,
        instagramUserId,
        linkedUserId: pickFirstNonEmpty(keep.linkedUserId, merge.linkedUserId),
        originCountry: pickFirstNonEmpty(keep.originCountry, merge.originCountry),
    };
}

export function buildChefMergePatch(keep: Chef, merge: Chef): Partial<Chef> {
    const preview = previewChefMerge(keep, merge);
    const websites = preview.websites;

    return {
        name: preview.name,
        englishName: preview.englishName,
        ...(preview.description ? { description: preview.description } : {}),
        ...(preview.imageUrl ? { imageUrl: preview.imageUrl } : {}),
        ...(websites.length ? { websites, website: websites[0] } : {}),
        ...(preview.instagramUsername ? { instagramUsername: preview.instagramUsername } : {}),
        ...(preview.instagramUserId ? { instagramUserId: preview.instagramUserId } : {}),
        ...(preview.linkedUserId ? { linkedUserId: preview.linkedUserId } : {}),
        ...(preview.originCountry ? { originCountry: preview.originCountry } : {}),
        updatedAt: new Date().toISOString(),
    };
}
